import { useEffect, useState } from 'react'
import { MoodEmoji } from '~/components/mood-emoji'
import { useCurrentUser } from '~/lib/auth/use-current-user'
import type {
  Checkin,
  CheckinUpdatePayload,
  DayCell,
  RangeUser,
} from '~/lib/api/types'
import { formatShortDate } from '~/lib/time/format'
import { useCheckin, useUpdateCheckin } from './use-checkin'
import styles from './side-panel.module.scss'

type DetailCell = Extract<
  DayCell,
  { kind: 'working' } | { kind: 'parse_failed' }
>

type Props = {
  cell: DetailCell
  user: RangeUser
  date: string
  onClose: () => void
}

export function SidePanel({ cell, user, date, onClose }: Props) {
  const { data: me } = useCurrentUser()
  const isAdmin = me?.user.is_admin ?? false
  const isSelf = me?.user.id === cell.user_id
  const canEdit = isAdmin || isSelf

  const [editing, setEditing] = useState(false)

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (editing) setEditing(false)
        else onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, editing])

  return (
    <>
      <div
        className={styles.scrim}
        role="presentation"
        onClick={() => (editing ? null : onClose())}
        aria-hidden
      />
      <aside
        className={styles.panel}
        role="dialog"
        aria-label={`Check-in detail for ${user.display_name ?? user.slack_user_id} on ${formatShortDate(date)}`}
      >
        <header className={styles.header}>
          <div>
            <p className={styles.engineer}>
              {user.display_name ?? user.slack_user_id}
            </p>
            <p className={`${styles.date} mono`}>{formatShortDate(date)}</p>
          </div>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label="Close detail"
          >
            ×
          </button>
        </header>

        {editing ? (
          <EditForm
            checkinId={cell.checkin_id}
            onSaved={() => setEditing(false)}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <ViewMode
            cell={cell}
            canEdit={canEdit}
            onEdit={() => setEditing(true)}
          />
        )}
      </aside>
    </>
  )
}

function ViewMode({
  cell,
  canEdit,
  onEdit,
}: {
  cell: DetailCell
  canEdit: boolean
  onEdit: () => void
}) {
  return (
    <>
      <dl className={styles.grid}>
        {cell.kind === 'working' && cell.mood && (
          <>
            <dt className={styles.label}>mood</dt>
            <dd className={styles.value}>
              <MoodEmoji mood={cell.mood} />
              {cell.mood_note && (
                <span className={styles.moodNote}>{cell.mood_note}</span>
              )}
            </dd>
          </>
        )}

        {cell.kind === 'working' && cell.plan_excerpt && (
          <>
            <dt className={styles.label}>plan</dt>
            <dd className={styles.value}>{cell.plan_excerpt}</dd>
          </>
        )}

        {cell.kind === 'working' &&
          cell.hours_parsed?.start &&
          cell.hours_parsed.end && (
            <>
              <dt className={styles.label}>hours</dt>
              <dd className={`${styles.value} mono`}>
                {cell.hours_parsed.start}–{cell.hours_parsed.end}
              </dd>
            </>
          )}

        {cell.kind === 'parse_failed' && (
          <>
            <dt className={styles.label}>raw hours</dt>
            <dd className={`${styles.value} mono`}>{cell.hours_raw ?? '—'}</dd>
            <dt className={styles.label}>status</dt>
            <dd className={styles.value}>parse {cell.parse_status}</dd>
          </>
        )}

        {cell.submitted_at && (
          <>
            <dt className={styles.label}>submitted</dt>
            <dd className={`${styles.value} mono`}>
              {new Date(cell.submitted_at).toLocaleString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                month: 'short',
                day: 'numeric',
              })}
            </dd>
          </>
        )}
      </dl>

      <div className={styles.actions}>
        {canEdit && (
          <button type="button" className={styles.editButton} onClick={onEdit}>
            Edit
          </button>
        )}
      </div>
    </>
  )
}

function EditForm({
  checkinId,
  onSaved,
  onCancel,
}: {
  checkinId: string
  onSaved: () => void
  onCancel: () => void
}) {
  const { data, isLoading, isError, refetch } = useCheckin(checkinId)
  const update = useUpdateCheckin(checkinId)

  if (isLoading) {
    return (
      <p className={styles.loading} aria-live="polite">
        Loading check-in…
      </p>
    )
  }

  if (isError || !data) {
    return (
      <p className={styles.errorText} role="alert">
        Couldn&apos;t load check-in.{' '}
        <button
          type="button"
          className={styles.linkButton}
          onClick={() => refetch()}
        >
          Retry →
        </button>
      </p>
    )
  }

  return (
    <EditFormFields
      initial={data}
      update={update}
      onSaved={onSaved}
      onCancel={onCancel}
    />
  )
}

function EditFormFields({
  initial,
  update,
  onSaved,
  onCancel,
}: {
  initial: Checkin
  update: ReturnType<typeof useUpdateCheckin>
  onSaved: () => void
  onCancel: () => void
}) {
  const [form, setForm] = useState({
    mood: initial.mood ?? '',
    mood_note: initial.mood_note ?? '',
    plan: initial.plan ?? '',
    blockers: initial.blockers ?? '',
    yesterday_wins: initial.yesterday_wins ?? '',
    hours_raw: initial.hours_raw ?? '',
    is_private: initial.is_private,
  })

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const payload: CheckinUpdatePayload = {
      mood: form.mood || null,
      mood_note: form.mood_note || null,
      plan: form.plan || null,
      blockers: form.blockers || null,
      yesterday_wins: form.yesterday_wins || null,
      hours_raw: form.hours_raw || null,
      is_private: form.is_private,
    }
    update.mutate(payload, { onSuccess: () => onSaved() })
  }

  return (
    <form
      className={styles.form}
      onSubmit={onSubmit}
      aria-label="Edit check-in"
    >
      <Field label="plan">
        <textarea
          className={styles.textarea}
          rows={3}
          value={form.plan}
          onChange={e => setForm({ ...form, plan: e.target.value })}
        />
      </Field>

      <Field label="hours_raw">
        <input
          className={styles.input}
          type="text"
          value={form.hours_raw}
          onChange={e => setForm({ ...form, hours_raw: e.target.value })}
        />
      </Field>

      <Field label="blockers">
        <textarea
          className={styles.textarea}
          rows={2}
          value={form.blockers}
          onChange={e => setForm({ ...form, blockers: e.target.value })}
        />
      </Field>

      <Field label="wins">
        <textarea
          className={styles.textarea}
          rows={2}
          value={form.yesterday_wins}
          onChange={e => setForm({ ...form, yesterday_wins: e.target.value })}
        />
      </Field>

      <Field label="mood">
        <input
          className={styles.input}
          type="text"
          placeholder=":smile:"
          value={form.mood}
          onChange={e => setForm({ ...form, mood: e.target.value })}
        />
      </Field>

      <Field label="mood note">
        <input
          className={styles.input}
          type="text"
          value={form.mood_note}
          onChange={e => setForm({ ...form, mood_note: e.target.value })}
        />
      </Field>

      <label className={styles.checkboxRow}>
        <input
          type="checkbox"
          checked={form.is_private}
          onChange={e => setForm({ ...form, is_private: e.target.checked })}
        />
        <span>Private (don&apos;t share to #checkins)</span>
      </label>

      {update.isError && (
        <p className={styles.errorText} role="alert">
          Save failed. Please try again.
        </p>
      )}

      <div className={styles.formActions}>
        <button
          type="button"
          className={styles.cancelButton}
          onClick={onCancel}
          disabled={update.isPending}
        >
          Cancel
        </button>
        <button
          type="submit"
          className={styles.saveButton}
          disabled={update.isPending}
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
    </label>
  )
}
